import { faAngleDown, faAngleUp } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { type ReactNode, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import styled from 'styled-components';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FullViewportScroll } from '@sorare/core/src/atoms/container/FullViewportScroll';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Horizontal, SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { useSimplifiedNav } from '@sorare/core/src/hooks/commonGame/useSimplifiedNav';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { useSo5Competitions } from '@sorare/core/src/hooks/useSo5Competitions';
import { useUnclaimedMarketTasksCount } from '@sorare/core/src/hooks/users/useUnclaimedMarketTasksCount';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { TextSwitchRarity } from 'atoms/TextSwitchRarity';

const AUTHORIZED_RARITIES = [
  Rarity.common,
  Rarity.limited,
  Rarity.rare,
  Rarity.super_rare,
  Rarity.unique,
];

const Sticky = styled(Horizontal)`
  position: sticky;
  top: var(--current-stack-height);
  z-index: 10;
  height: 0;
  justify-content: flex-end;
  align-items: flex-start;
`;

const Container = styled.div`
  --filter-height: 22px;
  --filter-min-width: var(--double-and-a-half-unit);
  display: grid;
  width: 100%;
  grid-template-columns: 74px 4fr;
  gap: 0;
  padding: var(--double-unit) 0;
  @media ${tabletAndAbove} {
    width: unset;
    display: flex;
    flex-direction: row-reverse;
    justify-content: flex-start;
    gap: var(--unit);
  }
`;

const StyledTabBar = styled(TabBar)`
  --fg-color: var(--c-white);
  --hover-bg-color: var(--c-nd-200-opaque);
  --active-fg-color: var(--c-nd-100);
  --active-bg-color: var(--c-nd-200);
  --bar-gap-custom: var(--unit);
  --default-bg-color: var(--c-black);

  &.fullWidth:not(.forceMobileLayout) > ul {
    padding-left: 0;
  }
`;

const RarityFilter = styled(Horizontal)`
  position: relative;
  isolation: isolate;
  z-index: 1;
  & ul {
    --bar-bg-color: var(--c-black);
  }
`;

const CompetitionLogo = styled.img`
  height: var(--filter-height);
  .selected & {
    filter: grayscale(1) invert(1);
  }
`;
const TabContent = styled(Horizontal)`
  min-width: var(--filter-min-width);
`;
const StyledStarBall = styled(StarBall)`
  height: var(--filter-height);
  width: var(--filter-height);
  .selected & {
    filter: grayscale(1) invert(1);
  }
`;
const StyledTab = styled(Tab)`
  flex-grow: 1 !important;
  & img {
    opacity: 1;
  }
`;

const Option = styled(SBHorizontal)`
  cursor: pointer;
  gap: var(--unit);
  min-width: 320px;
  max-width: 100%;
  padding: var(--unit) var(--intermediate-unit);
  transition: 0.2s ease-in-out;
  &:hover {
    background-color: var(--c-nd-50);
  }
`;

const Icon = styled.div`
  flex-shrink: 0;
  > * {
    height: var(--double-and-a-half-unit);
  }
`;

type SwitchCompetitionProps = {
  competitionSlug: string;
  onChangeCompetition: (c: string) => void;
  options: {
    label: ReactNode;
    selectedLabel: string;
    value: string;
  }[];
};
const SwitchCompetition = ({
  competitionSlug,
  onChangeCompetition,
  options,
}: SwitchCompetitionProps) => {
  const isTablet = useIsTabletAndAbove();
  if (isTablet) {
    const selectedOption = options.find(
      option => option.value === competitionSlug
    );
    return (
      <Dropdown
        gap={4}
        label={({ open, ...btnProps }) => (
          <Button
            disableDebounce
            color="tertiary"
            size="small"
            startIcon={selectedOption?.label}
            endIcon={<FontAwesomeIcon icon={open ? faAngleUp : faAngleDown} />}
            className={classNames({ active: open })}
            {...btnProps}
          >
            <LabelL>{selectedOption?.selectedLabel}</LabelL>
          </Button>
        )}
      >
        {({ closeDropdown }) => (
          <>
            {options.map(option => {
              return (
                <Option
                  key={option.value}
                  as={ButtonBase}
                  onClick={() => {
                    closeDropdown();
                    onChangeCompetition(option.value);
                  }}
                >
                  <Horizontal>
                    <Icon>{option.label}</Icon>
                    <LabelL color="var(--c-white)">
                      {option.selectedLabel}
                    </LabelL>
                  </Horizontal>
                </Option>
              );
            })}
          </>
        )}
      </Dropdown>
    );
  }
  return (
    <StyledTabBar
      fullWidth
      value={competitionSlug}
      variant="segmented"
      hideBackgroundWhenNoValue
      scrollable
      onValueChange={onChangeCompetition}
      grow
    >
      {options.map(option => (
        <StyledTab
          key={option.value}
          value={option.value}
          className={competitionSlug === option.value ? 'selected' : ''}
        >
          <TabContent center as="span">
            {option.label}
            {competitionSlug === option.value && (
              <LabelL bold>{option.selectedLabel}</LabelL>
            )}
          </TabContent>
        </StyledTab>
      ))}
    </StyledTabBar>
  );
};

export const Filters = ({ sport }: { sport: Sport }) => {
  const { showSimplifiedNav } = useSimplifiedNav(sport);
  const { scoutableSo5Competitions } = useSo5Competitions();
  const [searchParams, setSearchParams] = useSearchParams();
  const buyGemsParam = searchParams.get('buy_gems');
  const rarityParam = searchParams.get('rarity');

  const unclaimedMarketTasksCount = useUnclaimedMarketTasksCount();

  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const rarity =
    lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport]?.rarity || Rarity.limited;
  const competitionSlug =
    lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport]?.competitionSlug || '';

  const updateRarity = useCallback(
    (r: Rarity) => {
      updateLifecycle(LIFECYCLE.marketHomeFilters, {
        [sport]: {
          ...lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport],
          rarity: r,
        },
      });
    },
    [updateLifecycle, sport, lifecycle]
  );

  useEffect(() => {
    if (rarityParam === Rarity.common || buyGemsParam === 'true') {
      if (rarity !== Rarity.common) {
        updateRarity(Rarity.common);
      }
    }
    setSearchParams(prev => {
      prev.delete('rarity');
      return prev;
    });
  }, [rarityParam, buyGemsParam, rarity, updateRarity, setSearchParams]);

  useEffect(() => {
    if (showSimplifiedNav && rarity !== Rarity.common) {
      updateRarity(Rarity.common);
    }
  }, [showSimplifiedNav, rarity, updateRarity]);

  const options = [
    {
      label: <StyledStarBall />,
      selectedLabel: 'All Star',
      value: 'DEFAULT',
    },
    ...scoutableSo5Competitions.map(competition => ({
      label: (
        <CompetitionLogo
          src={competition.iconUrl}
          alt={competition.displayName}
        />
      ),
      selectedLabel: competition.displayName,
      value: competition.slug,
    })),
  ];

  const onChangeRarity = (r: Rarity) => {
    updateRarity(r);
  };
  const onChangeCompetition = (c: string) => {
    updateLifecycle(LIFECYCLE.marketHomeFilters, {
      [sport]: {
        ...lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport],
        competitionSlug: c,
      },
    });
  };

  const hideCompetitionFilter =
    scoutableSo5Competitions.length < 2 || rarity === Rarity.common;

  const rarities = showSimplifiedNav
    ? [Rarity.common]
    : AUTHORIZED_RARITIES.filter(r =>
        sport === Sport.BASEBALL ? r !== Rarity.common : true
      );
  const raritiesWithBadgeContent = rarities.map(r => ({
    value: r,
    badgeContent: r === Rarity.common ? unclaimedMarketTasksCount : undefined,
  }));

  return (
    <Sticky>
      <Container>
        <RarityFilter>
          <TextSwitchRarity
            rarities={raritiesWithBadgeContent}
            selectedRarity={rarity}
            onChange={onChangeRarity}
            transparent={false}
            variant="secondary"
            buttonSize="small"
          />
        </RarityFilter>

        {!hideCompetitionFilter && (
          <FullViewportScroll>
            <SwitchCompetition
              competitionSlug={competitionSlug}
              onChangeCompetition={onChangeCompetition}
              options={options}
            />
          </FullViewportScroll>
        )}
      </Container>
    </Sticky>
  );
};
